import { createSlice } from '@reduxjs/toolkit';
import {} from './atsJobDetailsActions';
import { PAGINATION } from '@/constants/pagination';

// todo: need to remove once api is done
const reducerName = 'atsJobDetails';
const initialState = {
  currentStage: {
    id: 'all_stages',
    name: 'All Stages',
  },
  currentMode: 'active',
  searchTerm: null,
  filters: {},
  reloadConfig: {},
  isFilterApplied: false,
  page: {
    number: 0,
    offset: 0,
    limit: PAGINATION.APPLICATION,
    scroll_token: null,
  },
  sort: {
    id: 'updated_at',
    label: 'Last activity (New to old)',
    element: 'updated_at',
    order: 'desc',
  },
  candidateList: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  kanbanList: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  getStagesCount: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  rejectedCount: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  createContact: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  currentApplication: {
    error: undefined,
    isLoading: true,
    status: undefined,
    data: {},
  },
  currentJobActivities: {
    error: undefined,
    isLoading: true,
    status: undefined,
    data: {},
  },
  currentJobActivitiesPage: {
    page: 0,
    limit: PAGINATION.APPLICATION,
  },
  currentApplicationActivities: {
    error: undefined,
    isLoading: true,
    status: undefined,
    data: {},
  },
  currentApplicationActivitiesPage: {
    page: 0,
    limit: PAGINATION.APPLICATION,
  },
  filterWithValues: [],
  scorecardParams: {
    department_ids: [],
    office_ids: [],
    employment_types: [],
  },
};

const { reducer, actions: atsJobDetailsActions } = createSlice({
  name: reducerName,
  initialState,

  reducers: {
    applySort: (state, { payload }) => {
      state.sort = payload;
      // Reset page and scroll_token when sort changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    resetSortToInitial: (state) => {
      state.sort = initialState?.sort;
    },

    applyAIReviewFilter: (state) => {
      state.filters = {
        is_ai_review_failed_applied: true,
      };
      state.filterWithValues = [
        {
          filter_by: {
            label: 'AI screening score status',
            value: 'ai_review_status',
          },
          filter_condition: {
            value: 'is',
            label: 'Include any',
          },
          filter_by_values: ['failed'],
        },
      ];
    },

    resetAIReviewFilter: (state) => {
      state.filters.is_ai_review_failed_applied = false;
      state.filterWithValues = state.filterWithValues.filter(
        (filter) => filter.filter_by.value !== 'ai_review_status'
      );
    },

    setCurrentStage: (state, { payload: { stage } }) => {
      state.currentStage = stage;
      // Reset page and scroll_token when stage changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      // Clear candidate list data to prevent old data from showing while loading new stage
      state.candidateList = {
        ...state.candidateList,
        data: {},
        isLoading: true,
      };
    },

    setCurrentMode: (state, { payload: { mode } }) => {
      state.currentMode = mode;
      // Reset page and scroll_token when mode changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      // Clear candidate list data to prevent old data from showing while loading new mode
      state.candidateList = {
        ...state.candidateList,
        data: {},
        isLoading: true,
      };
    },

    setReloadConfig: (state, { payload: { value } }) => {
      state.reloadConfig = value;
    },

    applyFilter: (state, { payload, ...props }) => {
      const { key, value } = payload;
      state.filters[key] = value;
      // Reset page and scroll_token when filter changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      state.filters.isFilterApplied = true;
    },

    setFiltersData: (state, { payload }) => {
      state.filterWithValues = payload;
    },

    applyJobFilter: (state, { payload }) => {
      state.filters = payload?.beConfig;
      // Reset page and scroll_token when filters change, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      state.filters.isFilterApplied = true;
    },

    applySearch: (state, { payload }) => {
      state.searchTerm = payload?.searchTerm;
      // Reset page and scroll_token when search changes, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    applyPage: (state, { payload }) => {
      const { page, scroll_token } = payload;
      state.page.number = page;
      state.page.offset = state?.page?.number * state?.page?.limit;
      // Update scroll_token if provided in payload, otherwise keep the existing one
      if (scroll_token !== undefined) {
        state.page.scroll_token = scroll_token;
      }
    },

    updateScrollToken: (state, { payload }) => {
      const { scroll_token } = payload;
      state.page.scroll_token = scroll_token;
    },

    applyLimit: (state, { payload }) => {
      const { limit } = payload;
      state.page.limit = limit;
      // Reset page to 0 and clear scroll_token when limit changes
      state.page.number = 0;
      state.page.offset = 0;
      state.page.scroll_token = null;
    },

    applyActivitiesPage: (state, { payload }) => {
      const { page } = payload;
      state.currentApplicationActivitiesPage.page = page;
    },

    applyJobsActivitiesPage: (state, { payload }) => {
      const { page } = payload;
      state.currentJobActivitiesPage.page = page;
    },

    clearFilter: (state, { payload }) => {
      const { key } = payload;
      state.filters[key] = initialState.filters[key];
      // Reset page and scroll_token when filter is cleared, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
      state.filters.isFilterApplied = Object.values(state.filters).some(
        (filter) => filter !== initialState.filters[key]
      );
      state.filters.isFilterApplied = false;
    },

    clearAllFilter: (state) => {
      state.filters = { ...initialState.filters };
      // Reset page and scroll_token when all filters are cleared, but preserve the current limit
      state.page = { ...initialState.page, limit: state.page.limit };
    },

    clearData: (state) => {
      state.sort = { ...initialState.sort };
      state.currentView = initialState.currentView;
      state.searchTerm = '';
    },

    increaseRejectCount: (state) => {
      state.rejectedCount.data.total_applications =
        +state?.rejectedCount?.data?.total_applications + 1;
    },

    setLoadingState: (state, { payload }) => {
      const { key, isLoading } = payload || {};
      if (state[key]) {
        state[key].isLoading = isLoading;
      }
    },

    setApiData: (state, { payload }) => {
      const { key, data, status, error, isLoading } = payload || {};
      if (state[key]) {
        state[key].data = data;
        state[key].isLoading = isLoading;
        state[key].status = status;
        if (error) {
          state[key].error = error;
        }
      }
      // Update scroll_token from candidateList API response for pagination
      // Only update if the value has changed to prevent unnecessary re-renders
      if (
        key === 'candidateList' &&
        data?.pagination?.scroll_token &&
        state.page.scroll_token !== data.pagination.scroll_token
      ) {
        state.page.scroll_token = data.pagination.scroll_token;
      }
      if (data?.ats_job?.ats_department_id) {
        state.scorecardParams = {
          department_ids: [data?.ats_job?.ats_department_id],
          office_ids: data?.ats_job?.ats_offices?.map((itr) => itr.id),
          employment_types: [data?.ats_job?.employment_type],
        };
      }
    },
  },
});

export { atsJobDetailsActions };
export default reducer;
